import React, { useState } from 'react';
import { RagIndicatorFamily } from '@/types/index';
import { IndicatorCard } from './IndicatorCard';

interface IndicatorFamilyProps {
  family: RagIndicatorFamily;
}

const getFamilyIcon = (familyKey: string): string => {
  switch (familyKey) {
    case 'financial': return '\uD83D\uDCB0';
    case 'technical': return '\uD83D\uDD27';
    case 'energy': return '\u26A1';
    case 'governance': return '\uD83C\uDFDB\uFE0F';
    case 'risk': return '\u26A0\uFE0F';
    default: return '\uD83D\uDCCA';
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 8) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (score >= 6) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  if (score >= 4) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  if (score >= 2) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
};

const getScoreBarColor = (score: number): string => {
  if (score >= 8) return 'bg-green-500';
  if (score >= 6) return 'bg-blue-500';
  if (score >= 4) return 'bg-yellow-500';
  if (score >= 2) return 'bg-orange-500';
  return 'bg-red-500';
};

export const IndicatorFamily: React.FC<IndicatorFamilyProps> = ({ family }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{getFamilyIcon(family.family)}</span>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              {family.label}
            </h3>
            {family.summary && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {family.summary}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Score badge */}
          <span className={`px-2.5 py-1 rounded-full text-sm font-bold ${getScoreColor(family.familyScore)}`}>
            {family.familyScore}/10
          </span>
          {/* Score bar */}
          <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${getScoreBarColor(family.familyScore)}`}
              style={{ width: `${family.familyScore * 10}%` }}
            />
          </div>
          {/* Chevron */}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Indicators */}
      {isExpanded && family.indicators.length > 0 && (
        <div className="px-4 pb-4 space-y-2">
          {family.indicators.map((indicator) => (
            <IndicatorCard key={indicator.name} indicator={indicator} />
          ))}
        </div>
      )}
    </div>
  );
};
